import useAlias from '@alias';
import Button from '@components/_elements/Button/Button';
import FormattedAlias from '@components/_shared/Translations/FormattedAlias';
import { faClone, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useGetDevicesFiltered, {
  UseGetDevicesProps,
} from '@hooks/api/queries/devices/useGetDevicesFiltered';
import useGetDevicesParentControllers from '@hooks/api/queries/devices/useGetDevicesParentControllers';
import useGetDevicesParents from '@hooks/api/queries/devices/useGetDevicesParents';
import useUseGetPointConfigsFilteredFromBroker from '@hooks/api/queries/point-configs/useGetPointConfigsFilteredFromBroker';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  styled,
  TextField,
} from '@mui/material';
import { Actions } from '@src/types/Actions';
import { Device } from '@src/types/Device';
import { SiteMeta } from '@src/types/SiteMeta';
import storeConnector from '@store/storeConnector';
import { HistorianDataPoint } from '@types';
import {
  HTMLAttributes,
  ReactElement,
  SyntheticEvent,
  useEffect,
  useMemo,
  useState,
} from 'react';

interface QueryDropdownsProps {
  disabled: boolean;
  actions: Actions;
  cloneDataPoint: (i: number, dp: HistorianDataPoint) => void;
  removeDataPoint: (i: number) => void;
  updateDataPoint: (i: number, dp: HistorianDataPoint) => void;
  dp: HistorianDataPoint;
  i: number;
  categories: string[];
  units: Device[];
  siteMeta: SiteMeta;
  hideDataPointDropdown: boolean;
}

const s = {
  label: {
    paddingTop: 25,
  },
} as const;

const axisOptions = ['Y1', 'Y2'];

const StyledAutoComplete = styled(Autocomplete)(() => ({
  '& div.MuiInputBase-root.MuiOutlinedInput-root': {
    padding: 0,
  },
  '& .MuiAutocomplete-endAdornment .MuiButtonBase-root': {
    visibility: 'visible',
  },
  '& .MuiAutocomplete-endAdornment .MuiButtonBase-root.MuiAutocomplete-popupIndicator .MuiSvgIcon-root':
    {
      fill: '#0b0b0f',
    },
  '& + .MuiAutocomplete-popper .MuiAutocomplete-paper': {
    width: 'fit-content',
    whiteSpace: 'nowrap',
  },
}));

type Option = {
  label: string;
  value: string;
  deviceId?: string;
  engineeringId?: string;
};

type OptionWithParentSourceDeviceNull = {
  name: string;
  parentSourceDeviceNull: boolean;
};

const renderOption = (
  props: HTMLAttributes<HTMLLIElement> & { key: any },
  option: unknown,
) => (
  <li {...props} key={(option as Option).value}>
    {(option as Option).label}
  </li>
);

const renderOptionUpperCased = (
  props: HTMLAttributes<HTMLLIElement> & { key: any },
  option: unknown,
) => (
  <li
    {...props}
    key={(option as OptionWithParentSourceDeviceNull)?.name ?? 'unknown'}
  >
    {(option as OptionWithParentSourceDeviceNull)?.name?.toUpperCase() ?? ''}
  </li>
);

const renderInput = (label: string, alias: (text: string) => string) => {
  function TextFieldComponent(params: AutocompleteRenderInputParams) {
    return (
      <TextField
        {...params}
        label={alias(label)}
        variant='standard'
        size='small'
      />
    );
  }

  return TextFieldComponent;
};

export function QueryDropdownsOnly(p: QueryDropdownsProps): ReactElement {
  const alias = useAlias();
  const categoryOptions = useMemo(
    () => [...new Set(p.categories?.map((cat) => cat).sort())],
    [p.categories],
  );

  const selectedCategoryOption = useMemo(
    () =>
      categoryOptions?.find(
        (categoryOption) =>
          (categoryOption as unknown as OptionWithParentSourceDeviceNull)
            ?.name === p.dp.category,
      ) ?? null,
    [p.dp.category, categoryOptions],
  );

  const [showUnitIdsDropdown, setShowUnitIdsDropdown] = useState(true);

  let dropdownNames = [
    'category',
    'unit',
    'parentSourceDeviceId',
    'sourceDevice',
    'dataPoint',
    'axis',
  ];
  const [showParentSourceDevicesDropdown, setShowParentSourceDevicesDropdown] =
    useState(false);
  const [parentSourceDeviceLabel, setParentSourceDeviceLabel] = useState('');
  const parentSiteControllerId = 'siteController_1';
  const parentSiteControllerEngId = p.siteMeta.siteId;
  const [deviceIdsFilter, setDeviceIdsFilter] = useState({
    body: {},
    options: {
      enabled: false,
      queryKeys: [''],
    },
  });

  const [showSourceDeviceIdsDropdown, setShowSourceDeviceIdsDropdown] =
    useState(false);

  if (p.dp.category && p.dp.category.includes('site')) {
    const elementsToRemove = ['unit', 'parentSourceDeviceId', 'sourceDevice'];
    dropdownNames = dropdownNames.filter(
      (item) => !elementsToRemove.includes(item),
    );
  }

  ///
  /// UNITS ///
  ///
  const { data: { devices: unitIds = [] } = {} } =
    useGetDevicesParentControllers({
      deviceType: p.dp.category,
      options: {
        enabled: Boolean(p.dp.category) && dropdownNames.includes('unit'),
      },
      queryKeys: [p.dp.category],
    });

  useEffect(() => {
    if (
      unitIds &&
      unitIds.length === 1 &&
      unitIds[0].deviceId.includes('siteController')
    ) {
      setShowUnitIdsDropdown(false);
    } else {
      setShowUnitIdsDropdown(true);
    }
  }, [unitIds]);

  const unitIdOptions: Option[] = useMemo(() => {
    if (!unitIds) {
      return [];
    }
    return unitIds
      .map(({ name, engineeringId }) => ({
        label: name,
        value: engineeringId,
      }))
      .sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { numeric: true }),
      );
  }, [unitIds]);

  const selectedUnitIdOption = useMemo(
    () =>
      unitIdOptions?.find(
        (unitIdOption) => unitIdOption.value === p.dp.unitId,
      ) ?? null,
    [p.dp.unitId, unitIdOptions],
  );

  const selectedUnitIdDeviceId = useMemo(
    () =>
      unitIds?.find(
        (unitIdOption) => unitIdOption.engineeringId === p.dp.unitId,
      ) ?? null,
    [p.dp.unitId, unitIds],
  );

  // engineering id
  const parentControllerEngineeringId =
    selectedUnitIdOption?.value || parentSiteControllerEngId || '';

  // device id only for http request
  const parentControllerDeviceId =
    selectedUnitIdDeviceId?.deviceId || parentSiteControllerId || '';

  ///
  /// PARENT SOURCE DEVICES ///
  ///
  const { data: { devices: parentSourceDevices = [] } = {} } =
    useGetDevicesParents({
      deviceType: p.dp.category,
      options: { enabled: !!p.dp.category },
      queryKeys: [
        p.dp.category,
        p.dp.unitId,
        ...(parentControllerDeviceId ? [parentControllerDeviceId] : []),
      ],
    });

  useEffect(() => {
    const hasUnitOrSiteController = parentSourceDevices?.some(
      (device) =>
        device.deviceId.includes('unit') || device.deviceId.includes('site'),
    );

    setShowSourceDeviceIdsDropdown(
      Boolean(p.dp.category) && !['site', 'unit'].includes(p.dp.category),
    );
    if (hasUnitOrSiteController) {
      setShowParentSourceDevicesDropdown(false);
    } else {
      if (!p.dp.category) {
        setShowParentSourceDevicesDropdown(false);
      } else {
        setShowParentSourceDevicesDropdown(true);
      }
      setParentSourceDeviceLabel(
        (parentSourceDevices &&
          parentSourceDevices[0] &&
          parentSourceDevices[0].name.split(' ')[0]) ||
          '',
      );
      setShowSourceDeviceIdsDropdown(true);
    }
  }, [p.dp.category, parentSourceDevices]);

  const parentSourceDevicesOptions: Option[] =
    parentSourceDevices
      ?.map(({ name, deviceId }) => ({
        label: name,
        value: deviceId,
      }))
      .sort((a, b) =>
        a.label.localeCompare(b.label, undefined, {
          numeric: true,
          sensitivity: 'base',
        }),
      ) || [];

  const parentSourceDevicesOption = useMemo(
    () =>
      parentSourceDevicesOptions?.find(
        (parentSourceDeviceOption) =>
          parentSourceDeviceOption.value === p.dp.parentSourceDeviceId,
      ) ?? null,
    [p.dp.parentSourceDeviceId, parentSourceDevicesOptions],
  );

  ///
  /// SOURCE DEVICE ID ///
  ///
  useEffect(() => {
    if (
      !showParentSourceDevicesDropdown &&
      !p.dp.parentSourceDeviceId &&
      parentControllerDeviceId
    ) {
      // from unit
      const devicesFilteredPayloadUnit = {
        body: {
          filter: {
            type: p.dp.category,
            parentControllerId: parentControllerDeviceId,
          },
        },
        options: {
          enabled: !!p.dp.unitId || parentControllerDeviceId.includes('site'),
          queryKeys: [
            p.dp.category,
            p.dp.unitId,
            'sourceDeviceId',
            parentControllerDeviceId,
          ],
        },
      };
      setDeviceIdsFilter(devicesFilteredPayloadUnit);
    } else {
      const devicesFilteredPayload = {
        body: {
          filter: {
            parentSourceDeviceId: p.dp.parentSourceDeviceId,
            parentControllerId: parentControllerDeviceId,
          },
        },
        options: {
          enabled: !!p.dp.parentSourceDeviceId,
          queryKeys: [
            p.dp.category,
            p.dp.unitId,
            'parentSourceDeviceId',
            p.dp.parentSourceDeviceId as string,
            ...(parentControllerDeviceId ? [parentControllerDeviceId] : []),
          ],
        },
      };
      setDeviceIdsFilter(devicesFilteredPayload);
    }
  }, [
    p.dp.unitId,
    p.dp.category,
    p.dp.parentSourceDeviceId,
    parentControllerDeviceId,
    showParentSourceDevicesDropdown,
  ]);

  const { data: sourceDeviceIds = [] } = useGetDevicesFiltered(
    deviceIdsFilter as UseGetDevicesProps,
  );

  const deviceIdsInputOptions = useMemo<Option[]>(
    () =>
      sourceDeviceIds
        .map(({ name, engineeringId }) => ({
          label: name,
          value: engineeringId,
        }))
        .sort((a, b) =>
          a.label.localeCompare(b.label, undefined, {
            numeric: true,
            sensitivity: 'base',
          }),
        ),
    [sourceDeviceIds],
  );

  const deviceIdsInputOption = useMemo(
    () =>
      deviceIdsInputOptions?.find(
        (sourceDeviceOption) =>
          sourceDeviceOption.value === p.dp.sourceDeviceId,
      ) ?? null,
    [p.dp.sourceDeviceId, deviceIdsInputOptions],
  );

  ///
  /// DATA POINTS ///
  ///
  const skipPointConfigFetchCondition = () => {
    return (
      !p.dp.category ||
      (showUnitIdsDropdown && !selectedUnitIdOption) ||
      (showParentSourceDevicesDropdown && !parentSourceDevicesOption) ||
      (showSourceDeviceIdsDropdown && !deviceIdsInputOption)
    );
  };

  const { data: pointConfigs = [] } = useUseGetPointConfigsFilteredFromBroker({
    body: {
      deviceType: p.dp.category,
      ...(p.dp.category !== 'siteController'
        ? { deviceId: p.dp.sourceDeviceId || parentControllerEngineeringId }
        : {}),
    },
    options: {
      enabled:
        p.dp.category.includes('site') || !skipPointConfigFetchCondition(),
      queryKeys: [
        p.dp.category,
        parentControllerEngineeringId,
        p.dp.sourceDeviceId || parentControllerDeviceId || '',
      ],
    },
  });

  const dataPointInputOptions = useMemo<Option[]>(
    () =>
      pointConfigs
        .map(({ pointName, displayName }) => ({
          label: displayName || pointName,
          value: pointName,
        }))
        .sort((a, b) =>
          a.label.localeCompare(b.label, undefined, {
            numeric: true,
            sensitivity: 'base',
          }),
        ),
    [pointConfigs],
  );

  const selectedDataPointInputOption = useMemo(
    () =>
      dataPointInputOptions.find(
        (dataPointInputOption) =>
          (typeof p.dp.dataPoint === 'string'
            ? p.dp.dataPoint
            : p.dp.dataPoint.pointName) === dataPointInputOption.value,
      ) ?? null,
    [p.dp.dataPoint, dataPointInputOptions],
  );

  const onChange = (field: string, value: string): void => {
    const updatedDataPoint = p.dp as HistorianDataPoint;
    switch (field) {
      case 'category':
        updatedDataPoint.category = value;
        updatedDataPoint.unitId = '';
        updatedDataPoint.parentSourceDeviceId = '';
        updatedDataPoint.sourceDeviceId = '';
        updatedDataPoint.dataPoint = '';
        break;
      case 'unitId':
        updatedDataPoint.unitId = value;
        updatedDataPoint.parentSourceDeviceId = '';
        updatedDataPoint.sourceDeviceId = '';
        updatedDataPoint.dataPoint = '';
        break;
      case 'parentSourceDeviceId':
        updatedDataPoint.parentSourceDeviceId = value;
        updatedDataPoint.sourceDeviceId = '';
        updatedDataPoint.dataPoint = '';
        break;
      case 'sourceDeviceId':
        if (p.dp.category && p.dp.category.includes('unit')) {
          p.dp.unitId = value;
        }
        updatedDataPoint.sourceDeviceId = value;
        updatedDataPoint.dataPoint = '';
        break;
      case 'dataPoint':
        updatedDataPoint.dataPoint =
          pointConfigs.find(({ pointName }) => pointName === value) ?? '';
        break;
      case 'axis':
        updatedDataPoint.axis = value;
        break;
      default:
        break;
    }
    p.updateDataPoint(p.i, updatedDataPoint);
  };

  const dropdowns = {
    category: {
      key: 'category',
      role: 'historian-category-dropdown',
      options: categoryOptions,
      disabled: false,
      sx: {
        width: 150,
        '& .MuiInputBase-input': { textTransform: 'uppercase' },
      },
      value: selectedCategoryOption,
      onChange: (_: SyntheticEvent<Element, Event>, value: unknown) =>
        onChange(
          'category',
          (value as OptionWithParentSourceDeviceNull)?.name ?? '',
        ),
      renderInput: renderInput('Category', alias),
      renderOption: renderOptionUpperCased,
      getOptionLabel: (option: unknown) =>
        (option as OptionWithParentSourceDeviceNull)?.name ?? '',
      show: true,
    },
    unit: {
      key: 'unitId',
      role: 'historian-unit-dropdown',
      disabled: false,
      options: unitIdOptions,
      sx: { width: 150 },
      value: selectedUnitIdOption,
      onChange: (_: SyntheticEvent<Element, Event>, value: unknown) => {
        onChange('unitId', (value as Option | null)?.value ?? '');
      },
      renderOption: renderOption,
      renderInput: renderInput('Unit', alias),
      getOptionLabel: (option: unknown) => (option as Option)?.label ?? '',
      show: showUnitIdsDropdown,
    },
    parentSourceDeviceId: {
      key: 'parentSourceDeviceId',
      role: 'historian-parent-source-device-id-dropdown',
      disabled: showUnitIdsDropdown && !selectedUnitIdOption,
      options: parentSourceDevicesOptions,
      sx: { width: 150 },
      value: parentSourceDevicesOption,
      onChange: (_: SyntheticEvent<Element, Event>, value: unknown) => {
        const selected = value as Option | null;
        if (selected) {
          onChange('parentSourceDeviceId', selected.value);
        }
      },
      renderOption: renderOption,
      renderInput: renderInput(
        `${parentSourceDeviceLabel.toUpperCase()} ID`,
        alias,
      ),
      getOptionLabel: (option: unknown) => (option as Option)?.label ?? '',
      show: showParentSourceDevicesDropdown,
    },
    sourceDevice: {
      key: 'sourceDeviceId',
      role: 'historian-source-device-id-dropdown',
      disabled:
        (showParentSourceDevicesDropdown && !parentSourceDevicesOption) ||
        (showUnitIdsDropdown && !selectedUnitIdOption),
      options: deviceIdsInputOptions,
      sx: { width: 150 },
      value: deviceIdsInputOption,
      onChange: (_: SyntheticEvent<Element, Event>, value: unknown) => {
        const selected = value as Option | null;
        if (selected) {
          onChange('sourceDeviceId', selected.value);
        }
      },
      renderOption: renderOption,
      renderInput: renderInput(
        `${p.dp.category ? p.dp.category.toUpperCase() : ''} ID`,
        alias,
      ),
      getOptionLabel: (option: unknown) => (option as Option)?.label ?? '',
      show: showSourceDeviceIdsDropdown,
    },
    dataPoint: {
      key: 'dataPoint',
      role: 'historian-point-name-dropdown',
      disabled: p.dp.category.includes('site')
        ? false
        : (showUnitIdsDropdown && !selectedUnitIdOption) ||
          (showParentSourceDevicesDropdown && !parentSourceDevicesOption) ||
          (showSourceDeviceIdsDropdown && !deviceIdsInputOption),
      options: dataPointInputOptions,
      sx: { width: 190 },
      value: selectedDataPointInputOption,
      onChange: (_: SyntheticEvent<Element, Event>, value: unknown) => {
        onChange('dataPoint', (value as Option | null)?.value ?? '');
      },
      renderOption: renderOption,
      renderInput: renderInput('Data Point', alias),
      getOptionLabel: (option: unknown) => (option as Option)?.label ?? '',
      show: !p.hideDataPointDropdown,
    },
    axis: {
      key: 'axis',
      role: 'historian-axis-dropdown',
      disabled: false,
      options: axisOptions,
      sx: { width: 150 },
      value: p.dp.axis || null,
      onChange: (_: SyntheticEvent<Element, Event>, value: unknown) =>
        onChange('axis', value as string),
      renderInput: renderInput('Axis', alias),
      renderOption: undefined,
      getOptionLabel: (option: unknown) =>
        typeof option === 'string' ? option : '',
      show: true,
    },
  };

  return (
    <>
      {dropdownNames.map((name) => {
        const dropdown = dropdowns[name as keyof typeof dropdowns];
        return (
          dropdown?.show && (
            <div key={dropdown.key} role={dropdown.role}>
              <StyledAutoComplete
                disablePortal
                disabled={dropdown.disabled}
                options={dropdown.options}
                sx={dropdown.sx}
                value={dropdown.value}
                onChange={dropdown.onChange}
                renderInput={dropdown.renderInput}
                renderOption={dropdown.renderOption}
                getOptionLabel={dropdown.getOptionLabel}
              />
            </div>
          )
        );
      })}
    </>
  );
}

function QueryDropdowns(p: QueryDropdownsProps): ReactElement {
  const alias = useAlias();
  return (
    <>
      <div className='label' style={s.label}>
        <span className='icon'>
          <FormattedAlias id='DataPoint' /> {p.i + 1}
        </span>
      </div>
      <div className='inputs-row dis'>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <QueryDropdownsOnly {...p} />
          <Button
            className='round-active-color'
            disabled={p.disabled}
            title={alias('Duplicate data point')}
            onClick={() => p.cloneDataPoint(p.i, p.dp)}
          >
            <FontAwesomeIcon icon={faClone} />
          </Button>
          <Button
            className='round-active-color'
            title={alias('Remove data point')}
            onClick={() => p.removeDataPoint(p.i)}
          >
            <FontAwesomeIcon icon={faTrash} />
          </Button>
        </div>
      </div>
    </>
  );
}

export default storeConnector(QueryDropdowns, {
  config: ['siteMeta'],
});
